/**
 * Homebrew Subclass Substrate
 *
 * Shared helpers for folding homebrew subclass features into SRD class data,
 * slugifying subclass names, and filtering class features by the selected
 * subclass. Consumed by the engine, server-srd, builder, and client-srd.
 *
 * D&D 5e System Reference Document 5.1
 * Licensed under Creative Commons Attribution 4.0 International (CC-BY-4.0)
 */
/**
 * Normalize a subclass name into a URL-safe slug.
 *
 * Lowercases, trims, converts spaces/underscores to "-", strips characters
 * other than [a-z0-9-], and collapses repeated "-".
 *
 * Mirrors src/components/character-hud/builder-mode/builder-helpers.ts slugify
 * so results match across the codebase.
 */
export function slugify(name) {
    return name
        .toLowerCase()
        .trim()
        .replace(/[\s_]+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-');
}
/**
 * Fold homebrew subclass features into the matching parent SRD classes.
 *
 * MUTATES the elements of the `classes` array (reassigns `cls.features`), but
 * NEVER mutates the shared feature array in place: a fresh array is assigned so
 * the shared module-constant SRD feature arrays are left untouched.
 *
 * For each entry: find the class whose slug === entry.data.parentClass, read
 * entry.data.subclassFeatures, and append them (mapped to SrdClassFeature and
 * tagged with subclass/subclassSlug). Entries whose parent class is not found
 * or that carry no subclass features are skipped.
 */
export function foldHomebrewSubclassFeatures(classes, subclassEntries) {
    for (const entry of subclassEntries) {
        const parentClass = entry.data.parentClass;
        const cls = classes.find((c) => c.slug === parentClass);
        if (!cls)
            continue;
        const rawFeatures = entry.data.subclassFeatures;
        if (!rawFeatures || rawFeatures.length === 0)
            continue;
        const mapped = rawFeatures.map((f) => ({
            name: f.name,
            level: f.level,
            description: f.description,
            subclass: entry.name,
            subclassSlug: entry.slug,
        }));
        // Fresh array — NEVER push into cls.features (shared SRD module constant).
        cls.features = [...cls.features, ...mapped];
    }
}
/**
 * Decide whether a class feature applies given the selected subclass.
 *
 * Base class features (no `subclass`) always apply. Subclass features are
 * excluded when no subclass is chosen, and otherwise included only when the
 * feature's owning subclass slug matches the selected one.
 */
export function includeClassFeature(f, selectedSubclassSlug) {
    if (!f.subclass)
        return true;
    if (!selectedSubclassSlug)
        return false;
    return (f.subclassSlug ?? slugify(f.subclass)) === selectedSubclassSlug;
}
